import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

/**
 * Small pill shown when the browser/Electron reports the network is down.
 * Combined with the persisted query cache (see src/lib/query-client.ts) and
 * the prescription outbox (src/lib/prescription-outbox.ts), users can keep
 * reading cached screens and capturing prescriptions without a connection.
 */
export function OfflineBanner() {
  const { t } = useLang();
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  if (online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-[100] -translate-x-1/2 flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-700 shadow-lg backdrop-blur dark:text-amber-300"
    >
      <WifiOff className="h-4 w-4" />
      <span>
        {t(
          "Offline — showing cached data. New prescriptions will sync when you reconnect.",
          "অফলাইন — সংরক্ষিত তথ্য দেখানো হচ্ছে। সংযোগ ফিরে এলে নতুন প্রেসক্রিপশন সিঙ্ক হবে।",
        )}
      </span>
    </div>
  );
}
